import { useEffect, useRef, useState } from 'react';
import { HIVE_SURGE_PULSE_DURATION_MS } from '../config/hiveSurgePulseEffect';

function mapHiveSurgeSlotToViewer(fx, slot, viewerUserId) {
  if (!fx || !slot || slot.zone == null || slot.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${slot.zone}`,
    index: slot.slotIndex,
  };
}

const HIVE_GROW_CHEVRON_STRATEGEM_IDS = new Set([62, 182, 224, 227]);

function isHiveSurgePulseStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (!HIVE_GROW_CHEVRON_STRATEGEM_IDS.has(Number(s.id))) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

/**
 * Strategems 62 (Hive Surge), 182 (Gift of the Rift), 224 (Divine Favor), 227 (Praise the Gods): HiveSurgePulseEffect grow chevrons
 * when `hiveSurgePulseFx.slots` is set (sounds differ per useStrategemSoundEffects).
 */
export function useHiveSurgePulse(gameState, viewerUserId) {
  const [activeHiveSurgePulses, setActiveHiveSurgePulses] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActiveHiveSurgePulses([]);
    }

    if (!seededRef.current) {
      Object.keys(gameState.state).forEach((pid) => {
        gameState.state[pid]?.completedOrders?.forEach((st) => {
          if (isHiveSurgePulseStrategem(st)) {
            processedRef.current.add(`hs${st.id}-${pid}-${st.instanceId}`);
          }
        });
      });
      seededRef.current = true;
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isHiveSurgePulseStrategem(strategem)) return;
        const fx = strategem.hiveSurgePulseFx;
        if (!fx || !Array.isArray(fx.slots) || fx.slots.length === 0) return;

        const dedupeKey = `hs${strategem.id}-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;
        processedRef.current.add(dedupeKey);

        fx.slots.forEach((slot, idx) => {
          const viewerSlot = mapHiveSurgeSlotToViewer(fx, slot, viewerUserId);
          if (!viewerSlot) return;

          const animKey = `hs${strategem.id}-${strategem.instanceId}-${idx}-${Date.now()}`;
          setActiveHiveSurgePulses((prev) => [
            ...prev,
            { key: animKey, viewerSlot, animationKey: animKey },
          ]);

          window.setTimeout(() => {
            setActiveHiveSurgePulses((prev) => prev.filter((p) => p.key !== animKey));
          }, HIVE_SURGE_PULSE_DURATION_MS + 120);
        });
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { activeHiveSurgePulses };
}

export default useHiveSurgePulse;
